/**
 * useVmDeletionNotifications Hook
 *
 * Tells the customer how a VM deletion ended, wherever they are in the console.
 *
 * A deletion is confirmed by n8n in the background, often after the customer has left the VM
 * pages for the dashboard or billing — pages that mount no resource listener at all. So the two
 * announcements live here, mounted once in the authenticated layout, and nowhere else:
 *
 * - deleted: a Decommissioning event the delete job marked `outcome: "Deleted"` (or, from older
 *   jobs, one leaving a Terminating row this client cached);
 * - refused: `failureCode: "DeletionFailed"` on the event that puts the VM back.
 *
 * The page hooks keep the cache work (patching, refreshing, leaving a deleted VM's detail page).
 * The status watch a page runs as a fallback announces through the same registry
 * (`lib/vmDeletionNotices`), so an outcome delivered by both is announced once.
 */

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import {
  socketIOManager,
  SocketIOEvent,
  type ResourceStatusEvent,
} from "@/lib/socketio";
import {
  DELETION_FAILED_CODE,
  isVmDeletionConfirmed,
  notifyVmDeleted,
  notifyVmDeletionFailed,
} from "@/lib/vmDeletionNotices";

export function useVmDeletionNotifications(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const unsubscribe = socketIOManager.subscribe<ResourceStatusEvent>(
      SocketIOEvent.ResourceStatusChanged,
      (event) => {
        const name = event.resourceName ?? event.resourceId;

        if (event.failureCode === DELETION_FAILED_CODE) {
          notifyVmDeletionFailed({
            resourceId: event.resourceId,
            name,
            at: event.timestamp,
          });
          return;
        }

        // The event carries no keep-until time; the status watch's read does, and whichever
        // road arrives first decides the wording.
        if (isVmDeletionConfirmed(event, queryClient)) {
          notifyVmDeleted({
            resourceId: event.resourceId,
            name,
            at: event.timestamp,
          });
        }
      },
    );

    return unsubscribe;
  }, [queryClient]);
}
